import React from 'react';
import { ASINSalesHistoryRow } from '@/types/asinSalesHistory';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { format } from 'date-fns';

interface ASINViewerChartProps {
  data: ASINSalesHistoryRow[];
}

export const ASINViewerChart: React.FC<ASINViewerChartProps> = ({ data }) => {
  // Prepare chart data - sort by date ascending for proper chart display
  const chartData = data
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map(row => ({
      date: format(new Date(row.date), 'MM/dd/yy'),
      revenue: row.revenue,
      grossProfit: row.grossProfit,
      ppcSpend: row.ppcSpend
    }));

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold">Revenue, PPC Spend and Gross Profit</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              style={{ fontSize: '12px' }}
              angle={-45}
              textAnchor="end"
              height={70}
            />
            {/* Left Y-Axis for Revenue and Gross Profit */}
            <YAxis 
              yAxisId="left"
              tickFormatter={formatCurrency}
              style={{ fontSize: '12px' }}
            />
            {/* Right Y-Axis for PPC Spend */}
            <YAxis 
              yAxisId="right"
              orientation="right"
              tickFormatter={formatCurrency}
              style={{ fontSize: '12px' }}
            />
            <Tooltip 
              formatter={(value: number) => formatCurrency(value)}
              labelStyle={{ color: '#000' }}
            />
            <Legend />
            <Line 
              yAxisId="left"
              type="monotone" 
              dataKey="revenue" 
              stroke="#3b82f6" 
              strokeWidth={2}
              name="Revenue"
              dot={{ r: 3 }}
            />
            <Line 
              yAxisId="left"
              type="monotone" 
              dataKey="grossProfit" 
              stroke="#ef4444" 
              strokeWidth={2}
              name="Gross Profit"
              dot={{ r: 3 }}
            />
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="ppcSpend" 
              stroke="#eab308" 
              strokeWidth={2}
              name="PPC Spend"
              dot={{ r: 3 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};